/**
 * Phase ribbons + captions at the top header (MapPhases.vue): a white (or
 * bluelight when selected) chevron pill per phase at PHASE_Y with a strip in
 * the state/status colour along its lower edge, plus the centred phase title
 * and — when there's room — the phase state glyph and an overdue clock, plus
 * the Eye (hide) and the before/after EyeOff (re-show hidden phases).
 */
import { PHASE_Y } from '@/store/remapStore';
import { isOverdue, todayIsoDate } from '@/utils/helpers/overdue';
import {
  canHidePhase,
  EYE_SIZE,
  hasHiddenPhasesAfter,
  hasHiddenPhasesBefore,
  phaseEyeCenter,
  phaseUnhideAfterCenter,
  phaseUnhideBeforeCenter,
} from '../../draw/hideEye';
import { drawIcon, drawStateIcon } from '../../draw/icons';
import {
  cssVar,
  modeOf,
  overdueColor,
  hexagonStatusFill,
} from '../../draw/palette';
import { phaseMarkerPath } from '../../draw/shapes';
import { truncate } from '../../draw/text';
import type { SceneContext } from '../../engine/SceneContext';

// WI #5550: hit padding around the drawn title text — the pointer affordance
// covers the text plus a few px, NOT the whole ribbon.
const TITLE_HIT_PAD = 3;
// Matches the `700 11px Roboto` caption font set below.
const TITLE_FONT_SIZE = 11;
// WI #5094: state glyph, status strip (ribbon height 48) and overdue clock.
const STATE_ICON_SIZE = 18;
const STATE_ICON_WIDTH = 26;
const STATE_ICON_RIM = 12;
const STRIP_Y = 43;
const STRIP_HEIGHT = 5;
const CLOCK_SIZE = 13;
const CLOCK_WIDTH = 18;

export function drawPhases(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
): void {
  const { map, io } = scene;
  const isTemplate = map.isTemplate;
  const today = todayIsoDate();
  const eyeOffColor = cssVar('--color-gray-600', '#475467');

  // EyeOff before the first visible phase (re-show the closest hidden one).
  if (hasHiddenPhasesBefore(io)) {
    const c = phaseUnhideBeforeCenter(
      scene.screenX(map.phaseStart(io.firstVisiblePhase)),
    );
    drawIcon(ctx, 'EyeOff', c.x, c.y, EYE_SIZE, eyeOffColor);
  }
  // EyeOff after the last visible phase.
  if (hasHiddenPhasesAfter(io)) {
    const last = io.lastVisiblePhase;
    const c = phaseUnhideAfterCenter(
      scene.screenX(map.phaseStart(last)),
      map.scaleX(map.phaseDuration(last)),
    );
    drawIcon(ctx, 'EyeOff', c.x, c.y, EYE_SIZE, eyeOffColor);
  }
  for (const phase of io.phases) {
    if (phase.isHiddenOnMap) {
      continue;
    }
    const x = scene.screenX(map.phaseStart(phase));
    const width = map.scaleX(map.phaseDuration(phase));
    if (x > scene.width + 20 || x + width < -20) {
      continue;
    }
    const selected = map.isSelectedItem('phase', phase.id);

    // Chevron ribbon.
    ctx.save();
    ctx.translate(x, PHASE_Y);
    const path = phaseMarkerPath(width);
    ctx.fillStyle = selected
      ? cssVar('--color-bluelight-500', '#0BA5EC')
      : '#FFFFFF';
    ctx.fill(path);
    // Same colours as milestones and activities in status view.
    const stripColor = isTemplate
      ? ''
      : hexagonStatusFill(modeOf(phase.state, phase.status));
    if (stripColor) {
      ctx.save();
      ctx.clip(path);
      ctx.fillStyle = stripColor;
      ctx.fillRect(0, STRIP_Y, width + 30, STRIP_HEIGHT);
      ctx.restore();
    }
    // Presentation mode outlines the ribbon for projector contrast
    // (MapPhaseMarker `.phase-marker[data-presentation='on']`).
    if (scene.ui.presentationMode === 'on') {
      ctx.lineWidth = 1;
      ctx.strokeStyle = '#000000';
      ctx.stroke(path);
    } else if (scene.exporting && !selected) {
      // On white PDF paper the white ribbon is invisible (on screen it reads
      // against the coloured backdrop). Give it a subtle outline — same idea as
      // the presentation-mode contrast border, softened for paper.
      ctx.lineWidth = 1;
      ctx.strokeStyle = cssVar('--color-gray-400', '#98A2B3');
      ctx.stroke(path);
    }
    ctx.restore();

    // Hide-eye (MapPhases' Eye → togglePhaseVisibility), on the first/last
    // visible phase, revealed only while the ribbon is hovered; click handled in
    // the hit-test.
    if (canHidePhase(io, phase.id) && scene.state.hoverPhaseId === phase.id) {
      const eye = phaseEyeCenter(x);
      drawIcon(
        ctx,
        'Eye',
        eye.x,
        eye.y,
        EYE_SIZE,
        selected
          ? cssVar('--color-gray-0', '#FFFFFF')
          : cssVar('--color-gray-600', '#475467'),
      );
    }

    // Caption: centred title (+ state glyph when wide enough).
    const maxWidth = width - 15;
    if (maxWidth <= 6) {
      continue;
    }
    const showIcon = !isTemplate && !!phase.state && maxWidth > 35;
    const iconWidth = isTemplate ? 0 : STATE_ICON_WIDTH;
    const showClock =
      !isTemplate &&
      maxWidth > 60 &&
      isOverdue(phase.deadline, phase.state, today);
    const clockWidth = showClock ? CLOCK_WIDTH : 0;
    ctx.font = '700 11px Roboto, sans-serif';
    const { text, width: textWidth } = truncate(
      ctx,
      phase.title,
      Math.max(0, maxWidth - iconWidth - clockWidth),
    );
    const centre = scene.screenX(
      map.phaseStart(phase) + map.phaseDuration(phase) / 2,
    );
    const startX = centre - (textWidth + iconWidth + clockWidth) / 2 + 6;

    if (showIcon) {
      const iconX = startX + 10;
      const iconY = PHASE_Y + 25;
      // White disc on the blue selected ribbon keeps the status colour readable.
      if (selected) {
        ctx.beginPath();
        ctx.arc(iconX, iconY, STATE_ICON_RIM, 0, Math.PI * 2);
        ctx.fillStyle = '#FFFFFF';
        ctx.fill();
      }
      drawStateIcon(
        ctx,
        phase.state,
        phase.status,
        iconX,
        iconY,
        STATE_ICON_SIZE,
      );
      scene.hits.addPhaseStateIcon(phase.id, iconX, iconY);
    }
    if (showClock) {
      drawIcon(
        ctx,
        'Clock',
        startX + iconWidth + textWidth + CLOCK_WIDTH / 2 + 3,
        PHASE_Y + 26,
        CLOCK_SIZE,
        selected ? cssVar('--color-gray-0', '#FFFFFF') : overdueColor(),
      );
    }
    ctx.fillStyle = selected
      ? cssVar('--color-gray-0', '#FFFFFF')
      : cssVar('--color-gray-700', '#344054');
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(text, startX + iconWidth, PHASE_Y + 30);

    // WI #5550: record the drawn (possibly truncated) title bounds so the
    // pointer affordance covers exactly the text. No text → no region.
    if (text) {
      scene.hits.addPhaseTitle(phase.id, {
        x: startX + iconWidth - TITLE_HIT_PAD,
        y: PHASE_Y + 30 - TITLE_FONT_SIZE - TITLE_HIT_PAD,
        w: textWidth + 2 * TITLE_HIT_PAD,
        h: TITLE_FONT_SIZE + 2 * TITLE_HIT_PAD,
      });
    }
  }
}
